import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Skeleton, CircularProgress } from '@mui/material';
import { EmailOutlined, CalendarToday, ArrowForward, PersonOutline, AccessTime, PeopleOutline, Block } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import TeamUpdates from '../components/TeamUpdates';
import BlockersRisks from '../components/BlockersRisks';
import AdditionalNotes from '../components/AdditionalNotes';
import pdfIcon from '../../../assets/images/pdf_icon.svg';
import inProgressIcon from '../../../assets/images/in_progress_icon.svg';
import notStartedIcon from '../../../assets/images/not_started_icon.svg';
import { fetchDailyStandupReport, sendDailyStandupReportEmail } from '../../../store/dailyStandupSlice';
import { setFilters, clearFilters, saveUserPreference } from '../../../store/ticketSlice';
import { COLORS } from '../../../shared/colors';
import DatePickerModal from '../../../shared/DatePickerModal';
import './ReportsTab.scss';

const ReportsTabSkeleton = () => (
  <Box className="skeleton-wrapper" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <Box className="reports-content-wrapper">
      {/* Summary row */}
      <Box className="summary-actions-row">
        <Box className="daily-summary">
          <Skeleton variant="text" width={260} height={24} className="skeleton-section-title" />
          <Box className="skeleton-meta-row">
            <Skeleton variant="text" width={90} height={18} />
            <Skeleton variant="text" width={90} height={18} />
            <Skeleton variant="text" width={120} height={18} />
          </Box>
        </Box>
        <Box className="export-actions">
          <Skeleton variant="rectangular" width={90} height={36} className="skeleton-action-btn" />
          <Skeleton variant="rectangular" width={130} height={36} className="skeleton-action-btn" />
        </Box>
      </Box>

      {/* Sprint Progress & Quick Actions */}
      <Box className="progress-actions-row">
        <Box className="sprint-progress-section">
          <Skeleton variant="text" width={130} height={24} className="skeleton-section-title" />
          <Box className="status-cards">
            {[1, 2, 3, 4].map(i => (
              <Box key={i} className="status-card">
                <Skeleton variant="text" width={70} height={18} />
                <Skeleton variant="text" width={50} height={28} />
              </Box>
            ))}
          </Box>
        </Box>
        <Box className="quick-actions-section">
          <Skeleton variant="text" width={110} height={24} className="skeleton-section-title" />
          <Box className="action-cards">
            <Skeleton variant="rectangular" width="100%" height={80} className="skeleton-action-card" />
            <Skeleton variant="rectangular" width="100%" height={80} className="skeleton-action-card" />
          </Box>
        </Box>
      </Box>

      {/* Team Detail table */}
      <Box>
        <Skeleton variant="text" width={150} height={24} className="skeleton-section-title" />
        <Box className="skeleton-table-wrapper">
          <Box className="skeleton-table-header">
            {[100, 200, 200, 90].map((w, i) => <Skeleton key={i} variant="text" width={w} height={18} />)}
          </Box>
          {[1, 2, 3, 4].map(row => (
            <Box key={row} className="skeleton-table-row">
              <Skeleton variant="circular" width={28} height={28} />
              <Skeleton variant="text" width={110} height={18} />
              <Skeleton variant="text" width={190} height={18} />
              <Skeleton variant="text" width={190} height={18} />
              <Skeleton variant="rectangular" width={70} height={22} className="skeleton-chip skeleton-ml-auto" />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Blockers */}
      <Box>
        <Skeleton variant="text" width={150} height={24} className="skeleton-section-title" />
        {[1, 2].map(i => (
          <Box key={i} className="skeleton-list-row">
            <Skeleton variant="circular" width={28} height={28} />
            <Skeleton variant="text" width={220} height={18} />
            <Skeleton variant="rectangular" width={70} height={22} className="skeleton-chip skeleton-ml-auto" />
          </Box>
        ))}
      </Box>

      {/* Additional Notes */}
      <Box>
        <Skeleton variant="text" width={140} height={24} className="skeleton-section-title" />
        {[1, 2].map(i => (
          <Box key={i} className="skeleton-notes-card">
            <Skeleton variant="text" width="88%" height={18} />
            <Skeleton variant="text" width="55%" height={18} />
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);


const getLocalISODate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatReportDate = (date) => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const normalizeTeamUpdates = (updates = []) => updates.map((update) => ({
  ...update,
  id: update.id || update.user_id,
  completed_yesterday: update.completed_yesterday || update.yesterday_plan || [],
  today_plan: update.today_plan || [],
}));

const ReportsTab = ({ onLogStandupClick, refreshKey = 0, projectId = null }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reportContentRef = useRef(null);
  
  const today = new Date();
  const todayStr = getLocalISODate(today);
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  
  const [stripStartDate, setStripStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 5);
    return d;
  });

  const {
    filters,
    lastSelectedProjectId,
    projectDropdownsByUser,
    projectDropdownsByUserLoading,
    projectDropdownsByUserError,
  } = useSelector((state) => state.tickets);
  const { report: dailyStandupReport, loading: reportLoading, error: reportError, emailLoading } = useSelector((state) => state.dailyStandup || {});
  const selectedProjectId = filters?.selectedProject;
  const effectiveProjectId = projectId || (
    selectedProjectId && String(selectedProjectId).toUpperCase() !== 'ALL'
      ? selectedProjectId
      : lastSelectedProjectId || projectDropdownsByUser?.[0]?.id || null
  );

  const selectedProject = useMemo(() => {
    if (!effectiveProjectId) return null;
    return projectDropdownsByUser?.find?.((item) => String(item.id) === String(effectiveProjectId)) || null;
  }, [effectiveProjectId, projectDropdownsByUser]);

  const isScrumProject = String(selectedProject?.project_type || '').toUpperCase() === 'SCRUM';
  const isSprintNull = !reportLoading && !dailyStandupReport?.report?.sprint;
  const shouldDisableActions = isScrumProject && isSprintNull;

  const handleViewBoard = () => {
    if (!effectiveProjectId) return;
    dispatch(clearFilters());
    
    const activeSprintId = dailyStandupReport?.report?.sprint?.id;
    const nextFilters = {
      selectedProject: effectiveProjectId,
    };
    if (isScrumProject && activeSprintId) {
      nextFilters.selectedSprint = [activeSprintId];
    }
    
    dispatch(setFilters(nextFilters));
    dispatch(saveUserPreference({
      preferenceKey: 'last_selected_project',
      preferenceValue: { project_id: effectiveProjectId }
    }));
    navigate('/ticket-management');
  };

  const isWaitingForProject = !effectiveProjectId && projectDropdownsByUserLoading;
  const loadError = reportError || (!effectiveProjectId && projectDropdownsByUserError);

  const reportData = dailyStandupReport || {};
  const report = reportData.report || {};
  const progress = reportData.progress || {};
  const teamUpdates = useMemo(() => normalizeTeamUpdates(reportData.team_updates || []), [reportData.team_updates]);
  const blockers = reportData.blockers || [];
  const additionalNotes = reportData.additional_notes || [];
  const reportDate = report.date ? new Date(report.date) : new Date(selectedDate);
  const formattedSelectedMonth = reportDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const formattedSelectedFull = reportDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  useEffect(() => {
    if (!effectiveProjectId) return;
    dispatch(fetchDailyStandupReport({
      projectId: effectiveProjectId,
      date: formatReportDate(selectedDate),
    }));
  }, [dispatch, effectiveProjectId, selectedDate, refreshKey]);

  const dates = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(stripStartDate);
    d.setDate(d.getDate() + i);
    return d;
  });

  const handlePrevDay = () => {
    const d = new Date(stripStartDate);
    d.setDate(d.getDate() - 1);
    setStripStartDate(d);
  };

  const handleNextDay = () => {
    const d = new Date(stripStartDate);
    d.setDate(d.getDate() + 1);
    setStripStartDate(d);
  };

  const defaultStripStartDate = new Date(today);
  defaultStripStartDate.setDate(defaultStripStartDate.getDate() - 5);

  const handleEmail = () => {
    if (!effectiveProjectId) {
      toast.error(t('dailyStandup.reports.selectProjectBeforeEmail'));
      return;
    }

    dispatch(sendDailyStandupReportEmail({
      projectId: effectiveProjectId,
      date: formatReportDate(selectedDate),
    }))
      .unwrap()
      .then(() => {
        toast.success(t('dailyStandup.emailSuccess'));
      })
      .catch((error) => {
        toast.error(typeof error === 'string' ? error : 'Failed to send report email.');
      });
  };

  const handleExportPDF = async () => {
    setPdfLoading(true);
    const exportReportToPDF = async () => {
      const reportElement = reportContentRef.current;
      if (!reportElement) {
        toast.error('Report content is not ready for export.');
        return;
      }

      try {
        const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
          import('html2canvas'),
          import('jspdf')
        ]);

        const loadingToastId = toast.loading('Generating PDF...');

        let canvas;
        try {
          canvas = await html2canvas(reportElement, {
            scale: window.devicePixelRatio || 2,
            useCORS: true,
            backgroundColor: COLORS.white,
            onclone: (clonedDoc, element) => {
              if (element) {
                element.classList.add('daily-standup-pdf-export');
                element.style.overflow = 'visible';
                element.style.height = 'auto';
                element.style.maxHeight = 'none';
              }

              const teamUpdatesContainer = clonedDoc.querySelector('.team-updates-container');
              if (teamUpdatesContainer) {
                teamUpdatesContainer.style.overflow = 'visible';
                teamUpdatesContainer.style.height = 'auto';
                teamUpdatesContainer.style.maxHeight = 'none';
                teamUpdatesContainer.style.borderRadius = '0';
                teamUpdatesContainer.style.boxShadow = 'none';
              }

              const teamUpdatesTableWrapper = clonedDoc.querySelector('.team-updates-container .table-wrapper');
              if (teamUpdatesTableWrapper) {
                teamUpdatesTableWrapper.style.overflow = 'visible';
                teamUpdatesTableWrapper.style.overflowY = 'visible';
                teamUpdatesTableWrapper.style.overflowX = 'visible';
                teamUpdatesTableWrapper.style.height = 'auto';
                teamUpdatesTableWrapper.style.maxHeight = 'none';
                teamUpdatesTableWrapper.style.width = '100%';
              }

              // ── De-sticky thead: html2canvas double-renders sticky elements when
              //    the parent overflow changes to visible, creating a gray phantom box ──
              clonedDoc.querySelectorAll('.team-updates-container thead').forEach((el) => {
                el.style.position = 'relative';
                el.style.zIndex = 'auto';
                el.style.backgroundColor = COLORS.white;
              });

              clonedDoc.querySelectorAll('.MuiCollapse-root, .MuiCollapse-vertical').forEach((node) => {
                node.style.display = 'block';
                node.style.height = 'auto';
                node.style.overflow = 'visible';
                node.style.visibility = 'visible';
              });

              clonedDoc.querySelectorAll('.MuiCollapse-wrapper, .MuiCollapse-wrapperInner').forEach((node) => {
                node.style.display = 'block';
                node.style.height = 'auto';
                node.style.overflow = 'visible';
                node.style.visibility = 'visible';
              });

              clonedDoc.querySelectorAll('.blockers-header .header-icon-btn, .notes-header .MuiIconButton-root').forEach((node) => {
                node.style.display = 'none';
              });

              clonedDoc.querySelectorAll('.quick-actions-section').forEach((node) => {
                node.style.display = 'none';
              });

              clonedDoc.querySelectorAll('.ticket-chip-more').forEach((node) => {
                node.remove();
              });

              clonedDoc.querySelectorAll('.ticket-chip-list').forEach((list) => {
                list.style.display = 'flex';
                list.style.flexDirection = 'column';
                list.style.alignItems = 'flex-start';
                list.style.gap = '8px';
                list.style.width = '100%';
              });

              clonedDoc.querySelectorAll('.ticket-chip-item').forEach((chip) => {
                chip.style.display = 'flex';
                chip.style.width = '100%';
                chip.style.maxWidth = '100%';
                chip.style.height = 'auto';
              });

              clonedDoc.querySelectorAll('.ticket-chip-wrapper').forEach((wrapper) => {
                wrapper.style.display = 'block';
                wrapper.style.overflow = 'visible';
                wrapper.style.width = '100%';
              });

              clonedDoc.querySelectorAll('.ticket-chip-title').forEach((title) => {
                title.style.whiteSpace = 'normal';
                title.style.textOverflow = 'clip';
                title.style.overflow = 'visible';
                title.style.maxWidth = 'none';
                title.style.height = 'auto';
                title.style.display = 'block';
              });

              const summaryActionsRow = clonedDoc.querySelector('.summary-actions-row');
              if (summaryActionsRow) {
                summaryActionsRow.style.display = 'block';
              }

              const exportActions = clonedDoc.querySelector('.export-actions');
              if (exportActions) {
                exportActions.style.display = 'none';
              }

              const progressActionsRow = clonedDoc.querySelector('.progress-actions-row');
              if (progressActionsRow) {
                progressActionsRow.style.display = 'block';
              }

              const sprintProgressSection = clonedDoc.querySelector('.sprint-progress-section');
              if (sprintProgressSection) {
                sprintProgressSection.style.width = '100%';
                sprintProgressSection.style.flex = '1 1 100%';
                sprintProgressSection.style.paddingRight = '0';
                sprintProgressSection.style.marginRight = '0';
              }

              const quickActionsSection = clonedDoc.querySelector('.quick-actions-section');
              if (quickActionsSection) {
                quickActionsSection.style.display = 'none';
              }
            }
          });
        } finally {
          // No class manipulation on the real reportElement
        }

        const margin = 8;
        const pageWidth = 210;
        const usableWidth = pageWidth - (margin * 2);
        const imgHeight = (canvas.height * usableWidth) / canvas.width;
        const pageHeight = imgHeight + (margin * 2);

        const pdf = new jsPDF('p', 'mm', [pageWidth, pageHeight]);
        const imgData = canvas.toDataURL('image/png');

        pdf.addImage(imgData, 'PNG', margin, margin, usableWidth, imgHeight);

        const datePart = report?.date || selectedDate;
        pdf.save(`daily-standup-report-${datePart || new Date().toISOString().split('T')[0]}.pdf`);
        toast.dismiss(loadingToastId);
        toast.success('PDF exported successfully.');
      } catch (error) {
        console.error('Error exporting daily standup PDF:', error);
        toast.dismiss();
        toast.error('Failed to export report to PDF.');
      }
    };

    try {
      await exportReportToPDF();
    } finally {
      setPdfLoading(false);
    }
  };



  return (
    <Box className="reports-tab-container">
      {/* Date Navigation Strip — always visible */}
      <Box className="date-navigation-section">
        <Box className="date-strip">
          <Box className="date-item nav-arrow" onClick={handlePrevDay}>
            <Typography className="day-label">&lt;</Typography>
          </Box>
          {dates
            .filter((d) => getLocalISODate(d) <= todayStr)
            .map((d) => {
              const dateStr = getLocalISODate(d);
              const isActive = dateStr === selectedDate;
              const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
              const dateNum = d.toLocaleDateString('en-US', { day: '2-digit' });
              return (
                <Box 
                  key={dateStr} 
                  className={`date-item ${isActive ? 'active' : ''}`}
                  onClick={() => setSelectedDate(dateStr)}
                >
                  <Typography className="day-label">{dayName}</Typography>
                  <Typography className="date-label">{dateNum}</Typography>
                  <Box className="dot" />
                </Box>
              );
            })}
          {getLocalISODate(stripStartDate) < getLocalISODate(defaultStripStartDate) && (
            <Box className="date-item nav-arrow" onClick={handleNextDay}>
              <Typography className="day-label">&gt;</Typography>
            </Box>
          )}
        </Box>
        <Box 
          className="calendar-picker" 
          onClick={(e) => setCalendarAnchor(e.currentTarget)}
          style={{ cursor: 'pointer' }}
        >
          <CalendarToday fontSize="small" />
          <Typography>{formattedSelectedMonth}</Typography>
        </Box>
        <DatePickerModal
          open={Boolean(calendarAnchor)}
          anchorEl={calendarAnchor}
          onClose={() => setCalendarAnchor(null)}
          onDateChange={(dates) => {
            if (dates && dates.length > 0) {
              const newDateStr = dates[0];
              setSelectedDate(newDateStr);
              const d = new Date(newDateStr);
              d.setDate(d.getDate() - 5);
              setStripStartDate(d);
            }
          }}
          selectedDates={[selectedDate]}
          title={t('calendar.selectDate', 'Select Date')}
          allowSingleSelection={true}
          autoApplySingleSelection={true}
          hideDueDate={true}
          hideCards={true}
          hideActions={true}
          maxDate={todayStr}
        />
      </Box>

      {/* Skeleton below date nav while loading */}
      {(reportLoading || isWaitingForProject) && <ReportsTabSkeleton />}

      {/* Actual content */}
      {!reportLoading && !isWaitingForProject && loadError && (
        <Box className="standup-no-data-state">
          <Typography className="standup-no-data-title">{t('dailyStandup.noData.title')}</Typography>
          <Typography className="standup-no-data-description">
            {typeof loadError === 'string' ? loadError : t('dailyStandup.noData.description')}
          </Typography>
        </Box>
      )}

      {!reportLoading && !isWaitingForProject && !loadError && (
      <Box className="reports-content-wrapper" ref={reportContentRef}>
        {/* Daily Summary & Actions */}
        <Box className="summary-actions-row">
          <Box className="daily-summary">
            <Typography className="summary-title">{formattedSelectedFull} - {t('dailyStandup.dailySummary.title')}</Typography>
            <Box className="summary-meta">
              <Box className="meta-item">
                <PersonOutline className="icon" /> {t('dailyStandup.dailySummary.members', { count: report.team_size || 0 })}
              </Box>
              <Box className="meta-separator" />
              <Box className="meta-item">
                <AccessTime className="icon" /> {report.generated_at || '--'}
              </Box>
              <Box className="meta-separator" />
              <Box className="meta-item">
                <PeopleOutline className="icon" /> {t('dailyStandup.dailySummary.facilitatedBy', { name: report.facilitator?.name || '--' })}
              </Box>
            </Box>
          </Box>
          <Box className="export-actions">
            <Button 
              variant="outlined" 
              className="action-btn" 
              startIcon={emailLoading ? <CircularProgress size={16} color="inherit" /> : <EmailOutlined />} 
              onClick={handleEmail}
              disabled={shouldDisableActions || emailLoading || pdfLoading}
            >
              {t('dailyStandup.exportActions.email')}
            </Button>
            <Button 
              variant="outlined" 
              className="action-btn" 
              startIcon={pdfLoading ? <CircularProgress size={16} color="inherit" /> : <img src={pdfIcon} alt="PDF" className="btn-icon" />} 
              onClick={handleExportPDF}
              disabled={shouldDisableActions || emailLoading || pdfLoading}
            >
              {t('dailyStandup.exportActions.exportToPdf')}
            </Button>
          </Box>
        </Box>

        {/* Sprint Progress & Quick Actions */}
        <Box className="progress-actions-row">
          <Box className="sprint-progress-section">
            <Typography className="section-title">{isScrumProject ? t('dailyStandup.sprintProgress.title') : t('dailyStandup.projectProgress.title')}</Typography>
            <Box className="status-cards">
              <Box className="status-card status-completed">
                <Box className="status-info">
                  <Box className="status-icon-wrapper">✓</Box>
                  {t('dailyStandup.sprintProgress.completed')}
                </Box>
                <Typography className="status-count"><span>{progress.completed?.count || 0}</span>/{progress.completed?.total || 0}</Typography>
              </Box>
              <Box className="status-card status-in-progress">
                <Box className="status-info">
                  <img src={inProgressIcon} alt="In Progress" className="status-img" />
                  {t('dailyStandup.sprintProgress.inProgress')}
                </Box>
                <Typography className="status-count"><span>{progress.in_progress?.count || 0}</span>/{progress.in_progress?.total || 0}</Typography>
              </Box>
              <Box className="status-card status-blocked">
                <Box className="status-info">
                  <Block className="status-icon" />
                  {t('dailyStandup.sprintProgress.blocked')}
                </Box>
                <Typography className="status-count"><span>{progress.blocked?.count || 0}</span>/{progress.blocked?.total || 0}</Typography>
              </Box>
              <Box className="status-card status-not-started">
                <Box className="status-info">
                  <img src={notStartedIcon} alt="Not Started" className="status-img" />
                  {t('dailyStandup.sprintProgress.notStarted')}
                </Box>
                <Typography className="status-count"><span>{progress.not_started?.count || 0}</span>/{progress.not_started?.total || 0}</Typography>
              </Box>
            </Box>
          </Box>
          <Box className="progress-separator-line" />
          <Box className="quick-actions-section">
            <Typography className="section-title">{t('dailyStandup.quickActions.title')}</Typography>
            <Box className="action-cards">
              <Box className="quick-action-card log-standup" onClick={onLogStandupClick}>
                <Typography variant="h4">{t('dailyStandup.quickActions.logStandup')}</Typography>
                <Typography>{t('dailyStandup.quickActions.logStandupDesc')}</Typography>
                <ArrowForward className="action-icon" />
              </Box>
              <Box className="quick-action-card view-board" onClick={handleViewBoard}>
                <Typography variant="h4">{isScrumProject ? t('dailyStandup.quickActions.viewBoard') : t('dailyStandup.quickActions.viewProjectBoard')}</Typography>
                <Typography>{isScrumProject ? t('dailyStandup.quickActions.viewBoardDesc') : t('dailyStandup.quickActions.viewProjectBoardDesc')}</Typography>
                <ArrowForward className="action-icon" />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Team Detail Table */}
        <TeamUpdates updates={teamUpdates} />

        {/* Blockers & Risks Accordion */}
        <BlockersRisks blockers={blockers} showCount={true} />

        {/* Additional Notes Accordion */}
        <AdditionalNotes notes={additionalNotes} showCount={true} />
      </Box>
      )}

    </Box>
  );
};

export default ReportsTab;
